import { isBackendConfigured, supabase } from './supabase'

/**
 * Global client-error reporter. Installs a single pair of listeners
 * (`window.onerror` + `window.onunhandledrejection`) that POST the
 * captured error to the `report_client_error` RPC. Admins watch the
 * "Recent client errors" panel of the god-view dashboard.
 *
 * Behaviour:
 *   • Fingerprint = sha-of-kind+message+location. The server dedupes
 *     identical (user, fingerprint) within a 5-minute window so a
 *     runaway loop doesn't bury the dashboard in dupes.
 *   • Local-only rate limit: at most one POST per 1s regardless of
 *     fingerprint. Saves bandwidth + Supabase quota in storms.
 *   • Best-effort fire-and-forget — the catch swallows any failure so
 *     a broken report path can't itself throw and recurse.
 *
 * Install once from main.tsx via `installErrorReporter()`.
 */

const MIN_GAP_MS = 1000
let lastPostAt = 0

export function installErrorReporter() {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (ev) => {
    void report('error', ev.message, ev.error?.stack ?? null, {
      filename: ev.filename, line: ev.lineno, col: ev.colno,
    })
  })

  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason
    const msg = reason instanceof Error ? reason.message : String(reason ?? 'Unknown rejection')
    const stack = reason instanceof Error ? reason.stack ?? null : null
    void report('rejection', msg, stack, {})
  })
}

/** Manual call — for caught-but-notable errors the calling code wants
 *  surfaced on the dashboard. Same dedup/rate-limit as the global path. */
export function reportManualError(message: string, extras?: { stack?: string; context?: Record<string, unknown> }) {
  void report('manual', message, extras?.stack ?? null, extras?.context ?? {})
}

async function report(
  kind: 'error' | 'rejection' | 'manual',
  message: string,
  stack: string | null,
  extra: Record<string, unknown>,
) {
  try {
    // Local rate-limit before we even build the payload.
    const nowMs = Date.now()
    if (nowMs - lastPostAt < MIN_GAP_MS) return
    lastPostAt = nowMs

    if (!isBackendConfigured || !supabase) return

    const ctx: Record<string, unknown> = {
      ...extra,
      url:         window.location?.href,
      userAgent:   navigator.userAgent,
      screen:      `${window.innerWidth}x${window.innerHeight}`,
    }
    // Fingerprint = kind + first 80 chars of message + URL path.
    // Cheap stable hash so the server-side dedup works.
    const fp = `${kind}::${(message || '').slice(0, 80)}::${window.location?.pathname ?? ''}`

    await supabase.rpc('report_client_error', {
      p_kind:        kind,
      p_message:     message,
      p_stack:       stack,
      p_context:     ctx,
      p_fingerprint: fp,
    })
  } catch {
    // Never let the reporter itself throw — that would re-enter the
    // window.onerror handler and loop.
  }
}
